import React, { useState, useMemo } from 'react';
import { Search, HelpCircle, BookOpen } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { helpData, HelpItem } from '@/data/help';
import { cn } from '@/lib/utils';

interface HelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HelpDialog({ open, onOpenChange }: HelpDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Group data by category
  const categories = useMemo(() => {
    const groups: Record<string, HelpItem[]> = {};
    helpData.forEach((item) => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
    });
    return groups;
  }, []);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    return helpData.filter((item) => {
      // Check title
      if (item.title.toLowerCase().includes(query)) return true;

      // Check keywords
      if (item.keywords?.some((k) => k.toLowerCase().includes(query)))
        return true;

      // Check content (if it's simple text, we can try to search it, but ReactNode is hard)
      // For now, let's stick to title and category matching for simplicity,
      // as searching inside ReactNode is complex without rendering strings.
      // We can add a plain text description field to HelpItem if needed later.

      return false;
    });
  }, [searchQuery]);

  const isSearching = searchQuery.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[80vh] flex-col gap-0 p-0 sm:max-w-[600px]">
        <DialogHeader className="border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            <DialogTitle>Documentation</DialogTitle>
          </div>
          <DialogDescription>
            Learn how to use Meshpit's features, editor, and parameter system.
          </DialogDescription>
          <div className="relative mt-2">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search help topics..."
              className="bg-muted/50 pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 p-6">
          {isSearching ? (
            <div className="space-y-6">
              {filteredItems.length > 0 ? (
                filteredItems.map((item) => (
                  <div key={item.id} className="space-y-2">
                    <h3 className="flex items-center gap-2 font-semibold text-primary">
                      {item.title}
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
                        {item.category}
                      </span>
                    </h3>
                    <div className="text-sm text-muted-foreground">
                      {item.content}
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <p>No results found for "{searchQuery}"</p>
                </div>
              )}
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full">
              {Object.entries(categories).map(([category, items]) => (
                <AccordionItem key={category} value={category}>
                  <AccordionTrigger className="text-base font-semibold">
                    {category}
                  </AccordionTrigger>
                  <AccordionContent className="space-y-6 pb-6 pt-2">
                    {items.map((item) => (
                      <div key={item.id} className="space-y-2">
                        <h4 className="font-medium text-foreground underline decoration-muted-foreground/30 underline-offset-4">
                          {item.title}
                        </h4>
                        <div className="border-l-2 border-muted pl-2 text-sm text-muted-foreground">
                          {item.content}
                        </div>
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
